import { useEffect, useState } from 'react';
import { Descriptions, DescriptionsProps } from 'antd';

// services
import { onGetShareTransactionDetails } from 'modules/users/services/users';
import NotificationService from 'core/services/notification';
import { isRTL } from './../../../../core/services/language';

// models
import { ShareTransaction } from 'modules/users/models/share-transaction';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { useTranslation } from 'react-i18next';

interface InputProps {
	transactionId: number;
	userId: number;
}

function ShareTransactionDetailsComponent({ transactionId, userId }: InputProps) {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [items, setItems] = useState<DescriptionsProps['items']>([]);
	const [transaction, setTransaction] = useState<ShareTransaction>();

	useEffect(() => {
		if (transactionId) {
			getTransactionDetails();
		}
	}, [transactionId]);

	const getTransactionDetails = async () => {
		try {
			setIsLoading(true);

			const response = await onGetShareTransactionDetails(transactionId, userId);

			const tempItems: DescriptionsProps['items'] = [
				{
					key: 'id',
					label: t('COMMON.ID'),
					children: response.data.data.id,
				},
				{
					key: 'company_country',
					label: t('USERS.COMPANY_COUNTRY'),
					children: response.data.data.company_country,
				},
				{
					key: 'company_sector',
					label: t('USERS.COMPANY_SECTOR'),
					children: response.data.data.company_sector,
				},
				{
					key: 'status_key',
					label: t('COMMON.STATUS'),
					children: response.data.data.status_key,
				},
				{
					key: 'transaction_role',
					label: t('USERS.TRANSACTION_ROLE'),
					children: response.data.data.transaction_role,
				},
				{
					key: 'seller_national_id',
					label: t('USERS.SELLER_NATIONAL_ID'),
					children: response.data.data.seller_national_id,
				},
				{
					key: 'quantity',
					label: t('USERS.QUANTITY'),
					children: response.data.data.quantity,
				},
				{
					key: 'order_type',
					label: t('USERS.ORDER_TYPE'),
					children: response.data.data.order_type,
				},
				{
					key: 'all_or_none',
					label: t('USERS.ALL_OR_NONE'),
					children: response.data.data.all_or_none,
				},
				{
					key: 'date_and_time ',
					label: t('USERS.DATE_AND_TIME'),
					children: response.data.data.date_and_time,
				},
				{
					key: 'expiry',
					label: t('USERS.EXPIRY'),
					children: response.data.data.expiry,
				},
				{
					key: 'price_per_share',
					label: t('USERS.PRICE_PER_SHARE'),
					children: response.data.data.price_per_share,
				},
				{
					key: 'total_sum',
					label: t('USERS.TOTAL_SUM'),
					children: response.data.data.total_sum,
				},
				{
					key: 'commission',
					label: t('USERS.COMMISSION'),
					children: response.data.data.commission,
				},
				{
					key: 'vat_percentage',
					label: t('USERS.VAT_PERCENTAGE'),
					children: response.data.data.vat_percentage,
				},
				{
					key: 'vat',
					label: t('USERS.VAT'),
					children: response.data.data.vat,
				},
				{
					key: 'estimated_order_value',
					label: t('USERS.ESTIMATED_ORDER_VALUE'),
					children: response.data.data.estimated_order_value,
				},
				{
					key: 'seller_accepted',
					label: t('USERS.SELLER_ACCEPTED'),
					children: response.data.data.seller_accepted ? t('USERS.ACCEPTED') : t('USERS.DECLINED'),
				},
				{
					key: 'buyer_accepted',
					label: t('USERS.BUYER_ACCEPTED'),
					children: response.data.data.buyer_accepted ? t('USERS.ACCEPTED') : t('USERS.DECLINED'),
				},
			];

			setItems(tempItems);

			setTransaction(response.data.data);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	return isLoading ? (
		<LoadingComponent />
	) : (
		<div className="mb-5">
			<div className="d-flex align-items-center gap-4 mt-3">
				<div>
					<h4>{isRTL() ? transaction?.meta?.localized_company_name?.ar : transaction?.meta?.localized_company_name?.en}</h4>
				</div>
			</div>

			<div className="mt-3">
				<Descriptions items={items} />
			</div>
		</div>
	);
}

export default ShareTransactionDetailsComponent;
